import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { formatEnumValue } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import type {
  QueueJob,
  QueueJobDetail,
  QueueJobState,
  SyncRunSummary,
  SyncStatus,
} from '@/features/chronograph-dashboard/types'

// =============================================================================
// Constants
// =============================================================================

export const LIVE_JOB_STATES: QueueJobState[] = ['waiting', 'active', 'delayed']

/**
 * Skip reason severity classification.
 * - healthy: Expected behavior, not a problem (e.g. already synced, budget scenario filtered)
 * - warning: Data quality issue worth investigating (e.g. null values, unknown period types)
 * - error: Something is broken or misconfigured (e.g. company not found, resolution failed)
 */
export type SkipSeverity = 'healthy' | 'warning' | 'error'

const HEALTHY_SKIP_REASONS = new Set(['ALREADY_SYNCED', 'BUDGET_SCENARIO'])
const ERROR_SKIP_REASONS = new Set([
  'COMPANY_NOT_FOUND',
  'LOCATION_RESOLUTION_FAILED',
])

export function getSkipSeverity(reason: string): SkipSeverity {
  if (HEALTHY_SKIP_REASONS.has(reason)) return 'healthy'
  if (ERROR_SKIP_REASONS.has(reason)) return 'error'
  return 'warning'
}

/** Severity sort order: errors first, warnings second, healthy last */
const SEVERITY_ORDER: Record<SkipSeverity, number> = {
  error: 0,
  warning: 1,
  healthy: 2,
}

export function sortSkipsBySeverity<T extends { reason: string }>(
  items: T[]
): T[] {
  return [...items].sort(
    (a, b) =>
      SEVERITY_ORDER[getSkipSeverity(a.reason)] -
      SEVERITY_ORDER[getSkipSeverity(b.reason)]
  )
}

/** Count non-healthy skip items */
export function countSkipIssues(
  items: Array<{ reason: string; count: number }>
): number {
  return items
    .filter((item) => getSkipSeverity(item.reason) !== 'healthy')
    .reduce((sum, item) => sum + item.count, 0)
}

// =============================================================================
// Pure Utility Functions
// =============================================================================

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
})

export function formatDateTime(date: string | null | undefined): string {
  if (!date) return '\u2014'
  const parsed = new Date(date)
  if (isNaN(parsed.getTime())) return '\u2014'
  return dateTimeFormatter.format(parsed)
}

export function formatRelativeTime(date: string | null | undefined): string {
  if (!date) return 'Never'
  const parsed = new Date(date)
  if (isNaN(parsed.getTime())) return 'Never'
  const now = Date.now()
  const diffMs = now - parsed.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

export function toShortId(id: string): string {
  return id.slice(0, 8)
}

export function isSameUtcDay(
  date: string | null | undefined,
  dayKey: string
): boolean {
  if (!date) return false
  const parsed = new Date(date)
  if (isNaN(parsed.getTime())) return false
  return parsed.toISOString().slice(0, 10) === dayKey
}

export function isPrimitiveValue(
  value: unknown
): value is string | number | boolean | null {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
}

export function formatParameterValue(
  value: string | number | boolean | null
): string {
  if (value === null) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

export function formatSyncTypeLabel(value: string): string {
  if (value === 'FULL' || value === 'full') {
    return 'Full Sync'
  }
  if (value === 'BACKFILL' || value === 'backfill') {
    return 'Historical Backfill'
  }
  return formatEnumValue(value)
}

export function isLiveJobState(state: QueueJobState): boolean {
  return LIVE_JOB_STATES.includes(state)
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function getProgressPercent(progress?: {
  percentComplete?: number
}): number | null {
  if (typeof progress?.percentComplete !== 'number') return null
  return Math.min(100, Math.max(0, progress.percentComplete))
}

export function getStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

export function formatJobRelativeTime(job: QueueJob): string {
  if (job.finishedAt) return `Finished ${formatRelativeTime(job.finishedAt)}`
  if (job.processedAt) return `Started ${formatRelativeTime(job.processedAt)}`
  return `Queued ${formatRelativeTime(job.createdAt)}`
}

export function computeDuration(
  start: string | null | undefined,
  end: string | null | undefined
): string | null {
  if (!start || !end) return null
  const startMs = new Date(start).getTime()
  const endMs = new Date(end).getTime()
  if (isNaN(startMs) || isNaN(endMs)) return null
  const diffMs = endMs - startMs
  if (diffMs < 0) return null
  const totalSeconds = Math.floor(diffMs / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes < 60)
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

export function hasJobDetail(
  job: QueueJob | QueueJobDetail | null
): job is QueueJobDetail {
  return Boolean(job && 'data' in job)
}

export function formatFieldLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

// =============================================================================
// Small Components
// =============================================================================

export function StatusBadge({
  status,
}: {
  status: SyncStatus | QueueJobState
}) {
  const normalized = status.toLowerCase()
  if (normalized === 'completed') {
    return <Badge variant="green">{formatEnumValue(status)}</Badge>
  }
  if (normalized === 'failed' || normalized === 'cancelled') {
    return <Badge variant="red">{formatEnumValue(status)}</Badge>
  }
  return (
    <Badge variant="outline" className="capitalize">
      {formatEnumValue(status)}
    </Badge>
  )
}

export function OverviewCard({
  label,
  value,
  meta,
  icon,
  tone = 'neutral',
  emphasize = false,
}: {
  label: string
  value: string
  meta?: string
  icon?: React.ReactNode
  tone?: 'neutral' | 'positive' | 'warning' | 'danger'
  emphasize?: boolean
}) {
  return (
    <Card
      className={cn(
        'border shadow-none',
        emphasize ? 'border-primary/18 bg-primary/[0.03]' : 'border-primary/12',
        tone === 'danger' && 'border-destructive/[0.13] bg-destructive/[0.02]',
        tone === 'warning' && 'border-amber-600/[0.13] bg-amber-600/[0.03]',
        tone === 'positive' && 'border-green-600/[0.13] bg-green-600/[0.02]'
      )}
    >
      <CardContent className="flex min-h-[112px] flex-col justify-between gap-2 px-4 py-3.5">
        <div className="flex items-center gap-2">
          {icon ? (
            <span
              className={cn(
                'inline-flex size-6 items-center justify-center rounded-full',
                tone === 'danger'
                  ? 'text-destructive'
                  : tone === 'warning'
                    ? 'text-amber-700'
                    : tone === 'positive'
                      ? 'text-green-600'
                      : 'text-primary/70'
              )}
            >
              {icon}
            </span>
          ) : null}
          <Typography
            variant="body-sm"
            className={cn(
              'uppercase text-primary/70',
              tone === 'warning' && 'text-amber-800',
              tone === 'danger' && 'text-destructive'
            )}
          >
            {label}
          </Typography>
        </div>
        <Typography
          variant="h4"
          as="p"
          className={cn(
            'font-inter text-[28px] leading-none tracking-tight tabular-nums',
            tone === 'warning' && 'text-amber-700',
            tone === 'danger' && 'text-destructive'
          )}
        >
          {value}
        </Typography>
        {meta ? (
          <Typography
            variant="body-sm"
            className={cn(
              'font-inter text-primary/70',
              tone === 'warning' && 'text-amber-800',
              tone === 'danger' && 'text-destructive'
            )}
          >
            {meta}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function DetailStatCard({
  label,
  value,
  meta,
}: {
  label: string
  value: string
  meta?: string
}) {
  return (
    <Card className="border-primary/12 bg-primary/[0.02] shadow-none">
      <CardContent className="space-y-2 px-4 py-4">
        <Typography variant="body-sm" className="uppercase text-primary/70">
          {label}
        </Typography>
        <Typography variant="h4" as="p" className="font-inter text-primary">
          {value}
        </Typography>
        {meta ? (
          <Typography variant="body-sm" className="text-primary/70">
            {meta}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function MetricPair({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Typography variant="body-sm" className="text-primary/70">
        {label}
      </Typography>
      <Typography variant="small" className="font-inter tabular-nums">
        {value}
      </Typography>
    </div>
  )
}

export function ImpactCell({ run }: { run: SyncRunSummary }) {
  const totalChanged = run.created + run.updated

  if (run.status === 'FAILED' || run.status === 'CANCELLED') {
    return <Badge variant="red">{formatEnumValue(run.status)}</Badge>
  }

  if (totalChanged > 0) {
    return (
      <div className="space-y-0.5">
        <Typography variant="small" className="font-inter tabular-nums">
          {totalChanged.toLocaleString()} changed
        </Typography>
        <Typography variant="body-sm" className="text-primary/70">
          {run.created.toLocaleString()} new &bull;{' '}
          {run.updated.toLocaleString()} updated
        </Typography>
      </div>
    )
  }

  if (run.skipped > 0) {
    return (
      <div className="space-y-0.5">
        <Badge variant="outline">No changes</Badge>
        <Typography variant="body-sm" className="text-primary/70">
          {run.skipped.toLocaleString()} skipped
        </Typography>
      </div>
    )
  }

  return <Badge variant="outline">No changes</Badge>
}

export function ProgressMeter({
  value,
  live = false,
  className,
}: {
  value: number | null
  live?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'h-2 overflow-hidden rounded-full bg-primary/[0.08]',
        className
      )}
    >
      <div
        className={cn(
          'h-full rounded-full bg-primary transition-all',
          live && value === null && 'w-1/3 animate-pulse bg-green-600'
        )}
        style={value !== null ? { width: `${value}%` } : undefined}
      />
    </div>
  )
}
